<template>
  <!--履歴書完成イメージ（ヘッダーの「履歴書完成イメージ」タブのアンカー先）-->
  <section id="resume-complete-image" class="resume_preview">
    <div class="renew3_wrapper">
      <h2 class="resume_preview_h2">履歴書完成イメージ</h2>
      <p class="resume_preview_sub">
        <span class="resume_preview_badge">最も人気</span>
        <span class="resume_preview_note"
          >履歴書の見本・記入例<span class="resume_preview_note_line"
            >(JIS規格・転職用)付</span
          ></span
        >
      </p>

      <!--見開き（左=1ページ目、右=2ページ目）。記入例は編集画面と同じプレビューで描く。
          静的トップには縦横比を固定した枠だけを焼き、中身が入っても高さがずれないようにする-->
      <div ref="spreadRef" class="resume_preview_spread">
        <!-- static:skip -->
        <top-resume-sample-spread v-if="isSpreadNear" />
        <!-- /static:skip -->
      </div>

      <div class="resume_preview_action">
        <a
          href="/format/rirekisho_job_change/edit/profile"
          id="toplnk_resume_complete_image"
          class="resume_preview_btn"
          >このテンプレートで履歴書を作成する</a
        >
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
/**
 * トップの「履歴書完成イメージ」帯。template は静的トップにもそのまま展開される
 * （scripts/extract-top-static.js）ので、バインディングを持たない素の HTML に保つ。
 *
 * 見開きの記入例（プレビュー本体・記入例データ・書式 CSS）は重いので、
 * 枠が画面に近づいてから遅延チャンクで読む。
 *
 * 別ページのヘッダータブから /#resume-complete-image で来たときは、Vue マウントで
 * DOM が差し替わり初期スクロールがずれるため、マウント後にもう一度アンカーへ寄せる。
 */
import { defineAsyncComponent, onMounted, ref } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useRoute } from 'vue-router/composables'

const TopResumeSampleSpread = defineAsyncComponent(
  () => import('@/components/top/TopResumeSampleSpread.vue')
)

const route = useRoute()

const spreadRef = ref<HTMLElement | null>(null)
const isSpreadNear = ref(false)
const { stop: stopObservingSpread } = useIntersectionObserver(
  spreadRef,
  ([entry]) => {
    if (!entry?.isIntersecting) return
    isSpreadNear.value = true
    stopObservingSpread()
  },
  { rootMargin: '400px 0px' }
)

onMounted(() => {
  if (route.hash !== '#resume-complete-image') return
  document.getElementById('resume-complete-image')?.scrollIntoView()
})
</script>

<style lang="scss" scoped>
.renew3_top .resume_preview {
  background: #fff8f1;
  padding: 60px 0 70px;
  margin-bottom: 60px;
}

.renew3_top .resume_preview_h2 {
  text-align: center;
  font-weight: bold;
  font-size: 24px;
  color: #004d40;
  margin-bottom: 16px;
}

.renew3_top .resume_preview_sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px 12px;
  margin-bottom: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: #333;
}

.renew3_top .resume_preview_badge {
  display: inline-block;
  padding: 4px 14px;
  border-radius: 999px;
  background: #e95b10;
  color: #fff;
  font-size: 12px;
  letter-spacing: 0.08em;
}

/* A4 2 ページ（794px x 2 + 余白 16px）x 1123px の縦横比。中身は TopResumeSampleSpread */
.renew3_top .resume_preview_spread {
  width: 100%;
  max-width: 816px;
  aspect-ratio: 1604 / 1123;
  margin: 0 auto 32px;
  filter: drop-shadow(0 4px 12px rgb(0 0 0 / 10%));
}

.renew3_top .resume_preview_action {
  text-align: center;
}

.renew3_top .resume_preview_btn {
  display: inline-block;
  min-width: 320px;
  padding: 14px 32px;
  border-radius: 999px;
  background: #0f9934;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  text-decoration: none;
  transition: transform 0.2s ease;
}

.renew3_top .resume_preview_btn:hover {
  transform: scale(1.03);
  opacity: 1;
  color: #fff;
  text-decoration: none;
}

/* SP */
@media screen and (width <= 640px) {
  .renew3_top .resume_preview {
    padding: 40px 0 48px;
    margin-bottom: 40px;
  }

  .renew3_top .resume_preview_h2 {
    font-size: 20px;
  }

  /* 「(JIS規格・転職用)付」は2行目に折り返す */
  .renew3_top .resume_preview_note_line {
    display: block;
  }

  .renew3_top .resume_preview_btn {
    display: block;
    min-width: 0;
    padding: 14px 16px;
  }
}
</style>
